import { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../styles/quill-custom.css';
import { ArrowLeft, Save, Loader2, Sparkles } from 'lucide-react';
import { propostaService, Proposta } from '../../../services/propostaService';
import { llmService } from '../../../services/llmService';
import api from '../../../services/api';
import { toLocalISODate } from '../../../utils/dateUtils';

interface Client {
  id: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
}

interface PropostaFormProps {
  proposta?: Proposta | null;
  onBack: () => void;
  onSaved: () => void;
  onAddClient?: () => void;
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'align', 'link', 'image',
];

export function PropostaForm({ proposta, onBack, onSaved, onAddClient }: PropostaFormProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isGerandoIa, setIsGerandoIa] = useState(false);
  const [isAjustando, setIsAjustando] = useState(false);
  const [showAjuste, setShowAjuste] = useState(false);
  const [instrucao, setInstrucao] = useState('');

  const [clientId, setClientId] = useState(proposta?.clientId || '');
  const [titulo, setTitulo] = useState(proposta?.titulo || '');
  const [dataEmissao, setDataEmissao] = useState(proposta?.dataEmissao?.split('T')[0] || toLocalISODate());
  const [dataValidade, setDataValidade] = useState(proposta?.dataValidade?.split('T')[0] || '');
  const [dataEntregaPrevista, setDataEntregaPrevista] = useState(proposta?.dataEntregaPrevista?.split('T')[0] || '');
  const [briefing, setBriefing] = useState(proposta?.briefing || '');
  const [conteudo, setConteudo] = useState(proposta?.conteudo || '');
  const [modoEditor, setModoEditor] = useState<'rich' | 'html'>('rich');
  const [valorGlobal, setValorGlobal] = useState(proposta?.valorGlobal ? String(proposta.valorGlobal) : '');

  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    api.get('/admin/clients')
      .then((response) => {
        const clientsData = response.data?.data || response.data;
        setClients(Array.isArray(clientsData) ? clientsData : []);
      })
      .catch(() => setClients([]));

    if (!proposta) {
      const validade = new Date();
      validade.setDate(validade.getDate() + 30);
      setDataValidade(toLocalISODate(validade));
    }
  }, [proposta]);

  const formatCurrencyInput = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  };

  const handleGerarIa = async () => {
    if (!briefing.trim()) return;
    setIsGerandoIa(true);
    try {
      const response = await llmService.gerarEscopo(briefing.trim());
      if (response.html) {
        setConteudo(response.html);
        // O editor visual (Quill) não suporta <table>; troca para o modo HTML
        // para preservar as tabelas quando o conteúdo as contém.
        if (response.html.includes('<table')) {
          setModoEditor('html');
        }
      }
    } catch (error: any) {
      console.error('Erro ao gerar escopo com IA:', error);
      const msg = error.response?.data?.error || error.message || 'Falha ao gerar escopo com IA.';
      alert(msg);
    } finally {
      setIsGerandoIa(false);
    }
  };

  const handleAjustarIa = async () => {
    if (!conteudo.trim() || !instrucao.trim()) return;
    setIsAjustando(true);
    try {
      const response = await llmService.ajustar(conteudo, instrucao.trim());
      if (response.html) {
        setConteudo(response.html);
        setInstrucao('');
        if (response.html.includes('<table')) {
          setModoEditor('html');
        }
      }
    } catch (error: any) {
      console.error('Erro ao ajustar com IA:', error);
      const msg = error.response?.data?.error || error.message || 'Falha ao ajustar com IA.';
      alert(msg);
    } finally {
      setIsAjustando(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId) {
      alert('Selecione um cliente');
      return;
    }

    if (!dataValidade) {
      alert('Informe a data de validade');
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        clientId,
        titulo: titulo.trim() || undefined,
        dataEmissao,
        dataValidade,
        dataEntregaPrevista: dataEntregaPrevista || undefined,
        briefing: briefing.trim() || undefined,
        valorGlobal: valorGlobal ? parseFloat(valorGlobal) : undefined,
        conteudo: conteudo || undefined,
      };

      if (proposta) {
        await propostaService.update(proposta.id, data);
      } else {
        await propostaService.create(data);
      }
      onSaved();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar proposta');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h2 className="text-xl font-bold">{proposta ? 'Editar Proposta' : 'Nova Proposta'}</h2>
        <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-4 py-2 btn btn-primary disabled:opacity-50">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar
        </button>
      </div>

      {/* Dados da Proposta */}
      <div className="bg-card border rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-foreground border-b pb-2">Dados da Proposta</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Cliente *</label>
            <div className="flex gap-2">
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="flex-1 px-3 py-2 border border-input rounded-lg text-foreground bg-card focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">Selecione um cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nomeFantasia || client.razaoSocial} - {client.cnpj}
                  </option>
                ))}
              </select>
              {onAddClient && (
                <button type="button" onClick={onAddClient} className="px-3 py-2 btn btn-primary" title="Cadastrar novo cliente">
                  +
                </button>
              )}
            </div>
            {clients.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">Nenhum cliente cadastrado.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Título / Assunto</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex: Sistema ERP – Fase 1"
              maxLength={255}
              className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Emissão</label>
            <input type="date" value={dataEmissao} onChange={(e) => setDataEmissao(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Validade</label>
            <input type="date" value={dataValidade} onChange={(e) => setDataValidade(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-1">
              Prazo de envio <span className="text-muted-foreground font-normal text-xs">(exibido como deadline no Zooy)</span>
            </label>
            <input type="date" value={dataEntregaPrevista} onChange={(e) => setDataEntregaPrevista(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
          </div>
        </div>
      </div>

      {/* Briefing e Escopo */}
      <div className="bg-card border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-foreground">Briefing e Escopo</h3>
          <button
            type="button"
            onClick={handleGerarIa}
            disabled={!briefing.trim() || isGerandoIa}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isGerandoIa ? (<><Loader2 className="w-4 h-4 animate-spin" /> Gerando...</>) : (<><Sparkles className="w-4 h-4" /> Gerar Escopo</>)}
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Briefing / Informações do Serviço
          </label>
          <textarea
            value={briefing}
            onChange={(e) => setBriefing(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card resize-y focus:ring-2 focus:ring-purple-500 text-sm"
            rows={6}
            placeholder="Descreva o serviço, objetivos, módulos, entregas, valores, condições de pagamento, prazos..."
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">Conteúdo da Proposta</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAjuste(s => !s)}
                disabled={!conteudo.trim()}
                title="Ajustar com IA"
                className="flex items-center justify-center p-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:from-purple-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isAjustando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => setModoEditor(m => m === 'rich' ? 'html' : 'rich')}
                className="text-xs px-2 py-1 rounded border border-input text-muted-foreground hover:text-foreground"
                title="Alternar entre editor visual e HTML bruto"
              >
                {modoEditor === 'rich' ? 'HTML' : 'Editor visual'}
              </button>
            </div>
          </div>

          {showAjuste && (
            <div className="border border-cyan-600/30 rounded-lg p-4 space-y-2 bg-cyan-50/40 mb-3">
              <label className="block text-sm font-medium text-foreground">
                Descreva o ajuste desejado no conteúdo da proposta
              </label>
              <textarea
                value={instrucao}
                onChange={(e) => setInstrucao(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card resize-y focus:ring-2 focus:ring-cyan-500 text-sm"
                rows={3}
                placeholder="Ex.: deixe o tom mais formal, remova a seção de garantia, adicione um parágrafo sobre prazos..."
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleAjustarIa}
                  disabled={!instrucao.trim() || isAjustando}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isAjustando ? (<><Loader2 className="w-4 h-4 animate-spin" /> Aplicando...</>) : ('Aplicar ajuste')}
                </button>
              </div>
            </div>
          )}

          {modoEditor === 'html' ? (
            <textarea
              value={conteudo}
              onChange={(e) => setConteudo(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card resize-y focus:ring-2 focus:ring-cyan-500 font-mono text-xs"
              rows={22}
              placeholder="Cole aqui o HTML da proposta..."
            />
          ) : (
            <div className="quill-proposta-editor" style={{ height: '450px' }}>
              {isGerandoIa && (
                <div className="absolute inset-0 bg-card/80 z-10 flex items-center justify-center rounded-lg">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                    <p className="text-sm text-muted-foreground">Gerando escopo com IA...</p>
                  </div>
                </div>
              )}
              <ReactQuill
                theme="snow"
                value={conteudo}
                onChange={setConteudo}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="O escopo gerado aparecerá aqui. Você pode editá-lo antes de salvar..."
              />
            </div>
          )}
        </div>
      </div>

      {/* Valor */}
      <div className="bg-card border rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-foreground border-b pb-2">Valor</h3>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Valor Total (R$)</label>
          <input
            type="text"
            inputMode="decimal"
            value={valorGlobal ? formatCurrencyInput(parseFloat(valorGlobal)) : ''}
            onChange={(e) => { const raw = e.target.value.replace(/\D/g, ''); const v = Number(raw) / 100; setValorGlobal(v > 0 ? String(v) : ''); }}
            className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card text-right focus:ring-2 focus:ring-cyan-500 text-sm"
            placeholder="0,00"
          />
        </div>
      </div>
    </form>
  );
}
